/**
 * Utility functions for formatting data
 */

// Format currency to Italian format
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format currency with cents
export const formatCurrencyWithCents = (amount: number): string => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Format date to Italian format
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Format datetime to Italian format
export const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format relative time (e.g., "2 giorni fa")
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    return 'Oggi';
  } else if (diffInDays === 1) {
    return 'Ieri';
  } else if (diffInDays < 7) {
    return `${diffInDays} giorni fa`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return weeks === 1 ? '1 settimana fa' : `${weeks} settimane fa`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return months === 1 ? '1 mese fa' : `${months} mesi fa`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return years === 1 ? '1 anno fa' : `${years} anni fa`;
  }
};

// Format number with thousands separator
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('it-IT').format(num);
};

// Format percentage
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

// Truncate text with ellipsis
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Capitalize first letter
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Convert string to title case
export const toTitleCase = (str: string): string => {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

// Format duration in minutes to human readable format
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}min`;
};

// Generate initials from name
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);
};

// Format tender status in Italian
export const formatTenderStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    'open': 'Aperto',
    'closed': 'Chiuso',
    'in_progress': 'In Corso',
    'draft': 'Bozza',
    'cancelled': 'Annullato',
    'awarded': 'Aggiudicato'
  };
  
  return statusMap[status.toLowerCase()] || capitalize(status);
};

// Get status color class
export const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    'aperto': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    'open': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    'chiuso': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    'closed': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    'in_corso': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    'in_progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    'bozza': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
    'draft': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
    'annullato': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    'aggiudicato': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    'awarded': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
  };
  
  return statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
};